package org.example.zone360.pages.players_Details.assistAI

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.VerticalAlign
import com.varabyte.kobweb.compose.css.fontWeight
import com.varabyte.kobweb.compose.css.textAlign
import com.varabyte.kobweb.compose.css.verticalAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.DefaultStyleSheet.style
import org.example.zone360.models.GetSinglePlayerData
import org.example.zone360.models.QuickAction
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.times
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

fun buildPlayerContext(player: GetSinglePlayerData): String {
    val trimmedAssessment = player.latestAssessment
        .lines()
        .map { it.trim() }
        .filter { it.isNotEmpty() }
        .joinToString(" ")

    val baseIntro = """
        ${player.firstName} ${player.lastName} is a ${player.age}-year-old football player from ${player.nationality}, 
        belonging to the ${player.ethnicity} ethnic background. 
        Standing at ${player.height} and weighing ${player.weight}, 
        ${player.firstName} currently plays for the team ${player.team}. 
        He wears jersey number ${player.number} and primarily plays as a ${player.position}, 
        with a secondary role as a ${player.secondaryPosition}. 
        He is right-footed and has a current player rating of ${player.rating}.
    """.trimIndent().replace("\n", " ")

    return if (trimmedAssessment.isEmpty()) {
        baseIntro
    } else {
        "$baseIntro Here is his last assessment attached. \n$trimmedAssessment"
    }
}
fun getQuickActionQuestions(position: String): Map<String, List<QuickAction>> = mapOf(

    "📊  Reporting" to listOf(
        QuickAction(
            title = "Complete Performance Review",
            query = """
        Analyze all of my Zone360 data, including coach evaluations, match reports, technical assessments, physical testing, mental evaluations, historical progress, and previous training plans.

        Create a complete performance review that answers:
        • What are my five biggest strengths?
        • What are my five biggest weaknesses?
        • Which areas have improved the most over the last 3, 6, and 12 months?
        • Which areas have plateaued?
        • Which areas are getting worse?
        • What trends do you notice?
        • What does my coach consistently praise?
        • What concerns appear repeatedly in my coach's assessments?
        • What habits are helping my development?
        • What habits are slowing my progress?

        Finish with:
        1. My biggest opportunity
        2. My biggest risk
        3. My Top 5 priorities for the next month.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "How Do I Earn More Playing Time?",
            query = """
        Using my complete Zone360 profile, explain why I am currently receiving my current amount of playing time.

        Identify:
        • What I already do well enough to earn minutes.
        • What is preventing me from playing more.
        • Which weaknesses matter the most.
        • Which weaknesses matter the least.
        • What my coach appears to value most.
        • Which improvements would most increase my playing time.
        • Which factors are outside my control.
        • Which factors I can immediately control.

        Create a 30-day improvement plan designed to maximize my chances of earning more minutes.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "Position Mastery",
            query = """
        Analyze my development as a $position.

        Compare my current abilities against the expectations for elite players of my age.

        Evaluate:
        Technical ability, Tactical understanding, Positioning, Decision making, Defensive actions, Offensive contribution, Communication, Leadership, Transition play, Consistency.

        Explain:
        • What I already perform at a high level.
        • Which position-specific skills need the most improvement.
        • Which mistakes repeatedly appear.
        • Which professional player my style most resembles and why.

        Build a development roadmap for the next 12 months.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "Next Level Blueprint",
            query = """
        Based on every assessment in Zone360, identify exactly what is preventing me from reaching the next level.

        Prioritize improvements by impact.

        Explain:
        • If I improve only ONE thing...
        • If I improve THREE things...
        • If I continue exactly as I am...

        Estimate how much each improvement could influence my overall performance.

        Build a weekly development roadmap.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "Mental Performance",
            query = """
        Analyze all coach comments and assessments related to my mentality.

        Evaluate:
        Confidence, Resilience, Competitive mindset, Leadership, Communication, Emotional control, Focus, Coachability, Discipline.

        Explain:
        • When I perform with confidence.
        • When confidence appears to drop.
        • Which situations affect me the most.
        • Which mental habits should I develop.

        Give me daily mental exercises.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "My Hidden Strengths",
            query = """
        Review all available Zone360 data.

        Identify strengths that I may not recognize.

        Explain:
        • Which abilities consistently appear in my assessments.
        • Which strengths separate me from other players.
        • Which strengths should become my playing identity.
        • Which strengths I should continue developing.
        • Which strengths are underutilized.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "Blind Spot Analysis",
            query = """
        Identify recurring weaknesses that appear throughout my coach assessments, match evaluations, testing, and historical reports.

        Explain:
        • Which mistakes happen repeatedly.
        • Why they occur.
        • What causes them.
        • Which weakness creates the biggest chain reaction.
        • Which weakness should be fixed first.

        Build a corrective training plan.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "My Weekly Training Plan",
            query = """
        Using my latest assessment and historical development, create my ideal training week.

        Include:
        Daily objectives, Technical sessions, Tactical work, Physical development, Recovery, Mental training, Nutrition, Sleep targets, Estimated training load.

        Explain why every session has been selected.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "Development Trend Analysis",
            query = """
        Analyze every assessment in chronological order.

        Show:
        • Skills improving fastest
        • Skills improving slowly
        • Skills declining
        • Coach feedback trends
        • Physical development trends
        • Mental development trends
        • Consistency trends

        Predict where my development is heading over the next year.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "Match Intelligence",
            query = """
        Review my match history and coach evaluations.

        Identify:
        • My best match
        • My weakest match
        • Common mistakes
        • Common successful actions
        • Decisions I consistently make well
        • Decisions I struggle with
        • Moments where I lose concentration
        • Tactical patterns

        Finish with three match objectives for my next game.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "Future Projection",
            query = """
        Using all historical Zone360 data, project my future development.

        Explain:
        • What level I am currently tracking toward
        • Whether my improvement is accelerating or slowing
        • Which milestones I should achieve in 3 months, 6 months, 12 months, and 24 months

        Explain what must happen to exceed these projections.
    """.trimIndent(),
            forceReport = true
        ),

        QuickAction(
            title = "Parent Development Report",
            query = """
        Create a report written specifically for my parents.

        Explain:
        • How their child has developed.
        • Which improvements are most encouraging.
        • Which concerns deserve attention.
        • Whether progress is appropriate for age.
        • What support should be provided at home.
        • What should not be emphasized.
        • Which conversations will help confidence.
        • Which habits parents should encourage.

        End with:
        "The 5 most important ways you can support your child's development this month."
    """.trimIndent(),
            forceReport = true
        )
    ),

    "🔥 Performance & Identity" to listOf(
        QuickAction(title = "How can I stand out in my position?", query = "How can I stand out in my position?"),
        QuickAction(title = "What defines a great $position at my age?", query = "What defines a great $position at my age?"),
        QuickAction(title = "How can I develop leadership on the field?", query = "How can I develop leadership on the field?"),
        QuickAction(title = "What should I focus on to reach the next level?", query = "What should I focus on to reach the next level?"),
        QuickAction(title = "What do coaches expect from a player like me?", query = "What do coaches expect from a player like me?")
    ),

    "⚽ Skill Development" to listOf(
        QuickAction(title = "What individual drills can I do to improve faster?", query = "What individual drills can I do to improve faster?"),
        QuickAction(title = "How do I strengthen my weaker foot?", query = "How do I strengthen my weaker foot?"),
        QuickAction(title = "What position-specific skills should I master?", query = "What position-specific skills should I master?"),
        QuickAction(title = "How often should I train outside of team practices?", query = "How often should I train outside of team practices?"),
        QuickAction(title = "Can you give me a weekly training routine to follow?", query = "Can you give me a weekly training routine to follow?")
    ),

    "🧠 Mental & Emotional Growth" to listOf(
        QuickAction(title = "What can I do when I feel low on confidence?", query = "What can I do when I feel low on confidence?"),
        QuickAction(title = "How do I manage nerves and pressure before games?", query = "How do I manage nerves and pressure before games?"),
        QuickAction(title = "What strategies help me stay focused in tough matches?", query = "What strategies help me stay focused in tough matches?"),
        QuickAction(title = "How do I mentally bounce back from a bad game?", query = "How do I mentally bounce back from a bad game?"),
        QuickAction(title = "What mindset habits lead to long-term success?", query = "What mindset habits lead to long-term success?")
    ),

    "🏃 Physical Growth & Conditioning" to listOf(
        QuickAction(title = "Am I physically ready for my age and position?", query = "Am I physically ready for my age and position?"),
        QuickAction(title = "How can I improve my speed, strength, and agility?", query = "How can I improve my speed, strength, and agility?"),
        QuickAction(title = "What should I eat before and after training?", query = "What should I eat before and after training?"),
        QuickAction(title = "How much recovery and sleep should I be getting?", query = "How much recovery and sleep should I be getting?"),
        QuickAction(title = "When should I expect to see physical improvements?", query = "When should I expect to see physical improvements?")
    ),

    "🎯 Game Management & Expectations" to listOf(
        QuickAction(title = "What is my role during a game as a $position?", query = "What is my role during a game as a $position?"),
        QuickAction(title = "What should I do if I'm not involved enough in the game?", query = "What should I do if I'm not involved enough in the game?"),
        QuickAction(title = "How do I manage my energy across both halves?", query = "How do I manage my energy across both halves?"),
        QuickAction(title = "How can I make an impact without scoring or assisting?", query = "How can I make an impact without scoring or assisting?"),
        QuickAction(title = "When is the right moment to press or hold my position?", query = "When is the right moment to press or hold my position?")
    ),

    "🧪 Prescribed Exercises" to listOf(
        QuickAction(title = "What drills can I do on my own to get better?", query = "What drills can I do on my own to get better?"),
        QuickAction(title = "Can you create a session based on my position and age?", query = "Can you create a session based on my position and age?"),
        QuickAction(title = "What physical skills should I focus on right now?", query = "What physical skills should I focus on right now?"),
        QuickAction(title = "What technical exercises will help me improve fastest?", query = "What technical exercises will help me improve fastest?"),
        QuickAction(title = "Can you build a weekly training plan for me?", query = "Can you build a weekly training plan for me?")
    ),

    "⚔️ Pre-Match Preparation" to listOf(
        QuickAction(title = "What should I do the day before a game?", query = "What should I do the day before a game?"),
        QuickAction(title = "What should I eat before the match, and when?", query = "What should I eat before the match, and when?"),
        QuickAction(title = "How can I mentally prepare to play with confidence?", query = "How can I mentally prepare to play with confidence?"),
        QuickAction(title = "What's a good warm-up routine for my position?", query = "What's a good warm-up routine for my position?"),
        QuickAction(title = "How do I handle nerves before kickoff?", query = "How do I handle nerves before kickoff?")
    ),

    "🧊 Post-Match Recovery & Reflection" to listOf(
        QuickAction(title = "What's the best way to recover after a match?", query = "What's the best way to recover after a match?"),
        QuickAction(title = "What should I eat or drink after a game?", query = "What should I eat or drink after a game?"),
        QuickAction(title = "How do I reflect on my performance the right way?", query = "How do I reflect on my performance the right way?"),
        QuickAction(title = "What cooldown or stretching should I do post-game?", query = "What cooldown or stretching should I do post-game?"),
        QuickAction(title = "How can I learn from mistakes without losing confidence?", query = "How can I learn from mistakes without losing confidence?")
    )
)

//fun getQuickActionQuestions(position: String): Map<String, List<String>> = mapOf(
//    "🔥 Performance & Identity" to listOf(
//        "How can I stand out in my position?",
//        "What defines a great $position at my age?",
//        "How can I develop leadership on the field?",
//        "What should I focus on to reach the next level?",
//        "What do coaches expect from a player like me?"
//    ),
//    "⚽ Skill Development" to listOf(
//        "What individual drills can I do to improve faster?",
//        "How do I strengthen my weaker foot?",
//        "What position-specific skills should I master?",
//        "How often should I train outside of team practices?",
//        "Can you give me a weekly training routine to follow?"
//    ),
//    "🧠 Mental & Emotional Growth" to listOf(
//        "What can I do when I feel low on confidence?",
//        "How do I manage nerves and pressure before games?",
//        "What strategies help me stay focused in tough matches?",
//        "How do I mentally bounce back from a bad game?",
//        "What mindset habits lead to long-term success?"
//    ),
//    "🏃 Physical Growth & Conditioning" to listOf(
//        "Am I physically ready for my age and position?",
//        "How can I improve my speed, strength, and agility?",
//        "What should I eat before and after training?",
//        "How much recovery and sleep should I be getting?",
//        "When should I expect to see physical improvements?"
//    ),
//    "🎯 Game Management & Expectations" to listOf(
//        "What is my role during a game as a $position?",
//        "What should I do if I’m not involved enough in the game?",
//        "How do I manage my energy across both halves?",
//        "How can I make an impact without scoring or assisting?",
//        "When is the right moment to press or hold my position?"
//    ),
//    "🧪 Prescribed Exercises" to listOf(
//        "What drills can I do on my own to get better?",
//        "Can you create a session based on my position and age?",
//        "What physical skills should I focus on right now?",
//        "What technical exercises will help me improve fastest?",
//        "Can you build a weekly training plan for me?"
//    ),
//    "⚔️ Pre-Match Preparation" to listOf(
//        "What should I do the day before a game?",
//        "What should I eat before the match, and when?",
//        "How can I mentally prepare to play with confidence?",
//        "What’s a good warm-up routine for my position?",
//        "How do I handle nerves before kickoff?"
//    ),
//    "🧊 Post-Match Recovery & Reflection" to listOf(
//        "What’s the best way to recover after a match?",
//        "What should I eat or drink after a game?",
//        "How do I reflect on my performance the right way?",
//        "What cooldown or stretching should I do post-game?",
//        "How can I learn from mistakes without losing confidence?"
//    )
//)
@Composable
fun SuperscriptDollarText(
    amount: String,
    fontSize: CSSLengthNumericValue = 50.px,
    textAlign: TextAlign = TextAlign.Center
) {
    Span({
        style {
            textAlign(textAlign)
            width(100.percent)
        }
    }) {
        Span(
            Modifier
                .fontSize(fontSize * 0.4f)
                .styleModifier { verticalAlign(VerticalAlign.Super) }
                .toAttrs()
        ) {
            Text("$")
        }

        Span(
            Modifier
                .fontSize(fontSize)
                .fontWeight(600)
                .toAttrs()
        ) {
            Text(amount)
        }
    }
}
